import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, X, Check, RotateCw, Edit2 } from "lucide-react";
import {
    isSpeechRecognitionSupported,
    createRecognizer,
    getStoredLang,
    setStoredLang,
    SUPPORTED_LANGS,
} from "@/lib/voiceInput";
import { trackEvent } from "@/lib/analytics";

/**
 * Reusable voice-capture button used in three places:
 *   • AI drawer input row      (surface="ai_drawer")
 *   • Home page search shim    (surface="home_search")
 *   • Posts page search input  (surface="posts_search")
 *
 * Renders:
 *   1. A circular mic button (orange idle, red pulsing while listening).
 *   2. A modal overlay while listening (interim transcript + language toggle).
 *   3. A confirmation card after recognition ends
 *      (Confirm & Search / Try again / Edit).
 *
 * `onConfirm(text, lang)` — fires when the user commits the transcript.
 * `onEdit(text)` — optional: fires when the user picks "Edit" — the caller
 *   should populate its own text field so the user can tweak before sending.
 * `size` — "sm" (36px, for suffix inside search inputs) or "md" (40px).
 */
export function VoiceCaptureButton({
    onConfirm,
    onEdit,
    surface = "ai_drawer",
    size = "md",
    className = "",
    testId = "voice-mic-btn",
    disabledReason = "",
}) {
    const supported = isSpeechRecognitionSupported();
    const [phase, setPhase] = useState("idle"); // idle | listening | review | error
    const [lang, setLang] = useState(() => getStoredLang());
    const [transcript, setTranscript] = useState("");
    const [interim, setInterim] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const recognizerRef = useRef(null);

    const teardown = useCallback(() => {
        try { recognizerRef.current?.stop(); } catch { /* ignore */ }
        recognizerRef.current = null;
    }, []);

    useEffect(() => () => teardown(), [teardown]);

    const startListening = useCallback(() => {
        setTranscript("");
        setInterim("");
        setErrorMsg("");
        setPhase("listening");
        trackEvent("search_submit", { source: "voice_start", surface });
        try {
            recognizerRef.current = createRecognizer({
                lang,
                onInterim: (t) => setInterim(t),
                onFinal: (t) => setTranscript(t),
                onError: (err) => {
                    let human = "Microphone error. Please try again.";
                    if (err === "not-allowed" || err === "permission-denied") {
                        human = "Microphone access denied. Enable it in your browser settings to use voice search.";
                    } else if (err === "no-speech") {
                        human = "I couldn't hear anything. Please try again.";
                    } else if (err === "network") {
                        human = "Voice recognition needs a network connection.";
                    }
                    setErrorMsg(human);
                    setPhase("error");
                    trackEvent("search_submit", { source: "voice_error", reason: err, surface });
                },
                onEnd: (finalText) => {
                    // Only advance to review if we captured something.
                    setPhase((p) => {
                        if (p === "error") return "error";
                        if (!finalText) {
                            setErrorMsg("I couldn't catch that. Please try again.");
                            return "error";
                        }
                        return "review";
                    });
                },
            });
            recognizerRef.current.start();
        } catch (e) {
            setErrorMsg(e?.message || "Voice search failed to start.");
            setPhase("error");
        }
    }, [lang, surface]);

    const stopListening = useCallback(() => {
        try { recognizerRef.current?.stop(); } catch { /* ignore */ }
    }, []);

    const cancel = useCallback(() => {
        teardown();
        setPhase("idle");
        setInterim("");
        setTranscript("");
        setErrorMsg("");
    }, [teardown]);

    const confirm = useCallback(() => {
        const text = (transcript || interim || "").trim();
        if (!text) { setPhase("error"); setErrorMsg("Empty transcript."); return; }
        trackEvent("search_submit", { source: "voice_confirmed", query: text, lang, surface });
        setPhase("idle");
        setTranscript("");
        setInterim("");
        onConfirm?.(text, lang);
    }, [transcript, interim, lang, surface, onConfirm]);

    const tryAgain = useCallback(() => {
        setTranscript("");
        setInterim("");
        setErrorMsg("");
        startListening();
    }, [startListening]);

    const edit = useCallback(() => {
        const text = (transcript || interim || "").trim();
        if (!text) return;
        setPhase("idle");
        setTranscript("");
        setInterim("");
        onEdit?.(text);
    }, [transcript, interim, onEdit]);

    const changeLang = useCallback((next) => {
        setLang(next);
        setStoredLang(next);
        if (phase === "listening") {
            // Restart with new lang — Web Speech doesn't allow mid-stream swap.
            stopListening();
            setTimeout(() => {
                recognizerRef.current = null;
                setPhase("idle");
                // Give the recognizer a beat to release the mic before restart.
                setTimeout(() => {
                    setLang(next);
                    startListening();
                }, 100);
            }, 150);
        }
    }, [phase, startListening, stopListening]);

    if (!supported) {
        // Gracefully hide the button on unsupported browsers (Firefox / Safari).
        return null;
    }

    const btnSize = size === "sm" ? "h-9 w-9" : "h-10 w-10";
    const listening = phase === "listening";

    return (
        <>
            <button
                type="button"
                onClick={startListening}
                aria-label={disabledReason || "Start voice search"}
                title={disabledReason || "Voice search"}
                data-testid={testId}
                disabled={!!disabledReason}
                className={
                    "flex shrink-0 items-center justify-center rounded-full transition " +
                    btnSize + " " +
                    (listening
                        ? "bg-rose-500 text-white shadow-md animate-pulse"
                        : "bg-[var(--brand-orange)] text-white shadow-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed") +
                    " " + className
                }
            >
                {disabledReason ? <MicOff size={size === "sm" ? 14 : 16} /> : <Mic size={size === "sm" ? 14 : 16} />}
            </button>

            {/* Overlay — rendered as a fixed portal-style block. */}
            {(phase === "listening" || phase === "review" || phase === "error") && (
                <VoiceOverlay
                    phase={phase}
                    lang={lang}
                    interim={interim}
                    transcript={transcript}
                    errorMsg={errorMsg}
                    onLangChange={changeLang}
                    onStop={stopListening}
                    onCancel={cancel}
                    onConfirm={confirm}
                    onTryAgain={tryAgain}
                    onEdit={onEdit ? edit : undefined}
                    testId={testId}
                />
            )}
        </>
    );
}

function VoiceOverlay({
    phase, lang, interim, transcript, errorMsg,
    onLangChange, onStop, onCancel, onConfirm, onTryAgain, onEdit, testId,
}) {
    return (
        <div
            className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center"
            role="dialog"
            aria-modal="true"
            data-testid={`${testId}-overlay`}
        >
            <div className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl">
                <button
                    onClick={onCancel}
                    aria-label="Close"
                    data-testid={`${testId}-close`}
                    className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-[var(--text-muted)] hover:bg-slate-200"
                >
                    <X size={14} />
                </button>

                {phase === "listening" && (
                    <div className="flex flex-col items-center gap-4" data-testid={`${testId}-listening`}>
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#FFF3E6]">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--brand-orange)] text-white shadow-lg animate-pulse">
                                <Mic size={32} />
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="font-heading text-lg font-extrabold text-[var(--text-primary)]">
                                Listening…
                            </div>
                            <div className="mt-1 font-body text-xs text-[var(--text-muted)]">
                                Speak now — I'll stop when you pause.
                            </div>
                        </div>

                        <div className="min-h-[3.5rem] w-full rounded-2xl bg-slate-50 px-4 py-3 text-center">
                            <p className="font-body text-sm italic text-[var(--text-primary)]" data-testid={`${testId}-interim`}>
                                {interim || <span className="text-[var(--text-muted)]">…</span>}
                            </p>
                        </div>

                        <LangChips lang={lang} onChange={onLangChange} testId={testId} />

                        <button
                            onClick={onStop}
                            data-testid={`${testId}-stop`}
                            className="btn-secondary rounded-full px-6 py-2"
                        >
                            Stop
                        </button>
                    </div>
                )}

                {phase === "review" && (
                    <div className="flex flex-col gap-4" data-testid={`${testId}-review`}>
                        <div className="text-left">
                            <div className="font-heading text-xs font-semibold uppercase tracking-wider text-[var(--brand-orange)]">
                                I heard
                            </div>
                            <div className="mt-2 rounded-2xl bg-neutral-900 px-4 py-4 font-heading text-base font-medium text-white shadow-inner">
                                <span data-testid={`${testId}-final-text`}>{transcript}</span>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="font-body text-[10px] uppercase tracking-wider text-[var(--text-muted)]">Language</span>
                                <span className="rounded-full bg-[#FFF3E6] px-2 py-0.5 font-heading text-[10px] font-semibold text-[var(--brand-orange)]">
                                    {SUPPORTED_LANGS.find((l) => l.code === lang)?.label || lang}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    data-testid={`${testId}-edit`}
                                    className="inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 font-heading text-sm font-semibold text-[var(--text-primary)] hover:bg-slate-50"
                                >
                                    <Edit2 size={12} /> Edit
                                </button>
                            )}
                            <button
                                onClick={onTryAgain}
                                data-testid={`${testId}-retry`}
                                className="inline-flex items-center justify-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 font-heading text-sm font-semibold text-[var(--text-primary)] hover:bg-slate-50"
                            >
                                <RotateCw size={12} /> Try again
                            </button>
                            <button
                                onClick={onConfirm}
                                data-testid={`${testId}-confirm`}
                                className="btn-primary inline-flex items-center justify-center gap-1 rounded-full px-5 py-2"
                            >
                                <Check size={14} /> Confirm &amp; Search
                            </button>
                        </div>
                    </div>
                )}

                {phase === "error" && (
                    <div className="flex flex-col items-center gap-4 text-center" data-testid={`${testId}-error`}>
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                            <MicOff size={26} />
                        </div>
                        <div>
                            <div className="font-heading text-lg font-bold text-[var(--text-primary)]">Voice search failed</div>
                            <p className="mt-1 font-body text-sm text-[var(--text-muted)]">{errorMsg}</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={onCancel} className="rounded-full border border-slate-200 bg-white px-4 py-2 font-heading text-sm font-semibold text-[var(--text-primary)] hover:bg-slate-50">
                                Close
                            </button>
                            <button onClick={onTryAgain} className="btn-primary rounded-full px-4 py-2 font-heading text-sm">
                                Try again
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function LangChips({ lang, onChange, testId }) {
    return (
        <div className="flex items-center gap-2" data-testid={`${testId}-lang-chips`}>
            {SUPPORTED_LANGS.map((l) => {
                const active = lang === l.code;
                return (
                    <button
                        key={l.code}
                        onClick={() => onChange(l.code)}
                        data-testid={`${testId}-lang-${l.code}`}
                        className={
                            "rounded-full border px-3 py-1 font-heading text-xs font-semibold transition " +
                            (active
                                ? "border-[var(--brand-orange)] bg-[var(--brand-orange)] text-white"
                                : "border-slate-200 bg-white text-[var(--text-primary)] hover:bg-slate-50")
                        }
                    >
                        {l.label}
                    </button>
                );
            })}
        </div>
    );
}

export default VoiceCaptureButton;
